import {
  ArrowsIcon,
  ClockIcon,
  NavIcons,
  PenIcon,
  PlusInSquareIcon,
  TrashAltIcon
} from "assets/icons";
import { GoBackHeaderTitle } from "components";
import { toastServerError } from "helpers";
import { useBoolean } from "hooks";
import { ContentLayout } from "layouts";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Loader, Modal } from "ui";

import {
  useDeleteMarketingFlow,
  useDeleteMarketingFlowStep,
  useMarketingFlow,
  useReorderMarketingFlowSteps,
  useRunMarketingFlows,
  useStopMarketingFlowContact,
  useUpdateMarketingFlowStatus
} from "../../apis";
import {
  EnrollContactModal,
  EnrollmentsTable,
  FlowModal,
  FlowStepModal,
  FlowStepsList,
  SectionCard,
  StatusDot
} from "../../components";
import {
  FLOW_STATUS_COLORS,
  FLOW_STATUS_LABELS,
  FLOW_TRIGGER_LABELS
} from "../../constants";
import {
  EMarketingFlowStatus,
  MarketingFlowEnrollment,
  MarketingFlowStep
} from "../../marketing.types";

import styles from "./MarketingFlowDetails.module.scss";

interface MarketingFlowDetailsProps {
  flowId: string;
}

const MarketingFlowDetails = ({ flowId }: MarketingFlowDetailsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { flow, steps, enrollments, isLoading } = useMarketingFlow(flowId);

  const {
    value: isFlowModalOpen,
    setTrue: openFlowModal,
    setFalse: closeFlowModal
  } = useBoolean(false);

  const {
    value: isStepModalOpen,
    setTrue: openStepModal,
    setFalse: closeStepModal
  } = useBoolean(false);

  const {
    value: isEnrollModalOpen,
    setTrue: openEnrollModal,
    setFalse: closeEnrollModal
  } = useBoolean(false);

  const [editableStep, setEditableStep] = useState<MarketingFlowStep | null>(
    null
  );
  const [stepToDelete, setStepToDelete] = useState<MarketingFlowStep | null>(
    null
  );
  const [isDeleteFlowModalOpen, setIsDeleteFlowModalOpen] = useState(false);
  const [enrollmentToStop, setEnrollmentToStop] =
    useState<MarketingFlowEnrollment | null>(null);

  const { mutate: updateFlowStatus, isLoading: isStatusUpdating } =
    useUpdateMarketingFlowStatus({
      onSuccess: (updated) =>
        toast.success(
          updated.status === EMarketingFlowStatus.Paused
            ? t("Flow paused — contacts in-flight keep progressing")
            : t("Flow resumed")
        ),
      onError: toastServerError
    });

  const { mutate: reorderSteps } = useReorderMarketingFlowSteps({
    onError: toastServerError
  });

  const { mutate: deleteStep, isLoading: isStepDeleting } =
    useDeleteMarketingFlowStep({
      onSuccess: () => {
        toast.success(t("Step deleted"));
        setStepToDelete(null);
      },
      onError: toastServerError
    });

  const { mutate: deleteFlow, isLoading: isFlowDeleting } =
    useDeleteMarketingFlow({
      onSuccess: () => {
        toast.success(t("Flow deleted"));
        navigate("/marketing/flows", { replace: true });
      },
      onError: toastServerError
    });

  const { mutate: stopEnrollment, isLoading: isStopping } =
    useStopMarketingFlowContact({
      onSuccess: () => {
        toast.success(t("Enrollment stopped"));
        setEnrollmentToStop(null);
      },
      onError: toastServerError
    });

  const { mutate: runFlows, isLoading: isRunning } = useRunMarketingFlows({
    onSuccess: () => toast.success(t("Scheduler run completed")),
    onError: toastServerError
  });

  const handleEditStep = (step: MarketingFlowStep) => {
    setEditableStep(step);
    openStepModal();
  };

  const handleCloseStepModal = () => {
    setEditableStep(null);
    closeStepModal();
  };

  if (isLoading || !flow) {
    return (
      <ContentLayout title={t("Flow")}>
        <Loader />
      </ContentLayout>
    );
  }

  const isPaused = flow.status === EMarketingFlowStatus.Paused;

  const cadence = steps.length
    ? steps
        .reduce<number[]>((acc, step) => {
          acc.push((acc[acc.length - 1] ?? 0) + step.delayDays);
          return acc;
        }, [])
        .map((total) => `J+${total}`)
        .join(" → ")
    : t("No steps yet — add the first one");

  return (
    <ContentLayout
      title={
        <GoBackHeaderTitle
          title={flow.name}
          onGoBack={() => navigate("/marketing/flows")}
        />
      }
      action={
        <div className={styles.headerActions}>
          <StatusDot
            color={FLOW_STATUS_COLORS[flow.status]}
            label={FLOW_STATUS_LABELS[flow.status]}
          />

          <Button
            variant="default"
            loading={isStatusUpdating}
            onClick={() =>
              updateFlowStatus({
                flowId,
                status: isPaused
                  ? EMarketingFlowStatus.Active
                  : EMarketingFlowStatus.Paused
              })
            }
          >
            {isPaused ? t("Resume") : t("Pause")}
          </Button>

          <Button
            variant="default"
            icon={<PenIcon width={16} height={16} />}
            onClick={openFlowModal}
          >
            {t("Edit")}
          </Button>

          <Button
            variant="delete"
            icon={<TrashAltIcon fill="#fff" width={16} height={16} />}
            onClick={() => setIsDeleteFlowModalOpen(true)}
          >
            {t("Delete")}
          </Button>
        </div>
      }
    >
      <div className={styles.details}>
        <SectionCard
          accentColor="#FF9900"
          icon={<ClockIcon />}
          title={t("Trigger & cadence")}
        >
          <div className={styles.details__triggerRow}>
            <span className={styles.details__triggerChip}>
              ⚡ {t(FLOW_TRIGGER_LABELS[flow.trigger])}
            </span>
            <span className={styles.details__stepsChip}>
              {steps.length} {t("steps")}
            </span>
          </div>

          <div className={styles.details__cadence}>
            <span className={styles.details__cadenceLabel}>
              {t("Cadence")}
            </span>
            <span className={styles.details__cadenceValue}>{cadence}</span>
          </div>
        </SectionCard>

        <SectionCard
          icon={<ArrowsIcon />}
          title={t("Sequence of steps")}
          action={
            <Button icon={<PlusInSquareIcon />} onClick={openStepModal}>
              {t("Add step")}
            </Button>
          }
        >
          <FlowStepsList
            steps={steps}
            onReorder={(stepIds) => reorderSteps({ flowId, stepIds })}
            onEdit={handleEditStep}
            onDelete={setStepToDelete}
          />
        </SectionCard>

        <SectionCard
          accentColor="#4A6CF7"
          icon={<NavIcons.ResellersIcon />}
          title={`${t("Enrollments")} (${enrollments.length})`}
          subtitle={t(
            "Contacts enroll automatically when the trigger fires (e.g. a new WhatsApp prospect); manual enroll is for testing. Run the scheduler to advance steps whose delay has elapsed — use a 0-day delay to test a step immediately."
          )}
          action={
            <div className={styles.details__sectionActions}>
              <Button
                variant="default"
                icon={<ClockIcon width={16} height={16} />}
                loading={isRunning}
                onClick={() => runFlows()}
              >
                {t("Run scheduler now")}
              </Button>

              <Button icon={<PlusInSquareIcon />} onClick={openEnrollModal}>
                {t("Enroll contact")}
              </Button>
            </div>
          }
        >
          <EnrollmentsTable
            enrollments={enrollments}
            onStop={setEnrollmentToStop}
          />
        </SectionCard>

      </div>

      <FlowModal
        isOpen={isFlowModalOpen}
        editableFlow={flow}
        onClose={closeFlowModal}
      />

      {isStepModalOpen && (
        <FlowStepModal
          isOpen={isStepModalOpen}
          flowId={flowId}
          editableStep={editableStep}
          onClose={handleCloseStepModal}
        />
      )}

      <EnrollContactModal
        isOpen={isEnrollModalOpen}
        flowId={flowId}
        onClose={closeEnrollModal}
      />

      <Modal
        type="delete"
        isOpen={Boolean(stepToDelete)}
        isLoading={isStepDeleting}
        title={t("Delete step")}
        okText={t("Delete")}
        onClose={() => setStepToDelete(null)}
        onOk={() =>
          stepToDelete && deleteStep({ flowId, stepId: stepToDelete.id })
        }
      >
        <p className={styles.confirmText}>
          {t("Delete this step? Contacts currently waiting on it move on according to the remaining steps.")}
        </p>
      </Modal>

      <Modal
        type="delete"
        isOpen={isDeleteFlowModalOpen}
        isLoading={isFlowDeleting}
        title={t("Delete flow")}
        okText={t("Delete")}
        onClose={() => setIsDeleteFlowModalOpen(false)}
        onOk={() => deleteFlow(flowId)}
      >
        <p className={styles.confirmText}>
          {`${t("Delete flow")} "${flow.name}"? ${t(
            "All its steps and enrollments will be removed."
          )}`}
        </p>
      </Modal>

      <Modal
        type="delete"
        isOpen={Boolean(enrollmentToStop)}
        isLoading={isStopping}
        title={t("Stop enrollment")}
        okText={t("Stop")}
        onClose={() => setEnrollmentToStop(null)}
        onOk={() =>
          enrollmentToStop &&
          stopEnrollment({ flowId, contactId: enrollmentToStop.contactId })
        }
      >
        <p className={styles.confirmText}>
          {t("Stop this contact's journey in the flow? They will receive no further steps.")}
        </p>
      </Modal>
    </ContentLayout>
  );
};

export default MarketingFlowDetails;
